import React, { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useGamePublishers, useUpdatePublisherGamev2 } from "@seabrookstudios/pitch2table";
import { PublisherGameStatusLabel } from "@seabrookstudios/pitch2table-core";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import { CircularProgressSize } from "../../theme";
import { PublisherInterestStatusSelect } from "./PublisherInterestStatusSelect";
import CircularProgress from "@mui/material/CircularProgress";
import CardActions from "@mui/material/CardActions";

/**
 * @type {Set<import("@seabrookstudios/pitch2table-core").PublisherGameStatus>}
 */
const FINAL_OR_INACTIVE_STATUSES = new Set(["Signed", "Lost", "Withdrawn", "Declined", "NotAcceptingSubmissions"]);

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.publisherGame
 */
export const PublisherInterestStatusUpdate = ({ publisherGame }) => {
  const { data: publishers = [] } = useGamePublishers(publisherGame.gameId);
  const updatePublisherGame = useUpdatePublisherGamev2(publisherGame.id);
  const [pendingStatus, setPendingStatus] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherGameStatus|null} */ (null)
  );
  const [showDeclinedFeedbackModal, setShowDeclinedFeedbackModal] = useState(false);

  const publishersToMarkLost = useMemo(
    () =>
      publishers.filter(
        (publisher) => publisher.id !== publisherGame.id && !FINAL_OR_INACTIVE_STATUSES.has(publisher.status)
      ),
    [publishers, publisherGame.id]
  );

  const affectedPublisherCount = publishersToMarkLost.length;
  const open = pendingStatus === "Signed" && affectedPublisherCount > 0;

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherGameStatus} status
   * @param {boolean|undefined} cleanUpOtherPublisherInterest
   */
  const saveStatus = async (status, cleanUpOtherPublisherInterest = undefined) => {
    /** @type {import("@seabrookstudios/pitch2table-core").UpdatePublisherGameRequest} */
    const request = {
      status,
      cleanUpOtherPublisherInterest,
      rules: undefined,
      sellsheet: undefined,
      sentPNP: undefined,
      sizzle: undefined,
      signedDate: undefined,
      externalPortalUrl: undefined,
      nextActionType: undefined,
      expectedReleaseDate: undefined,
      publisherProvidedFeedback: undefined,
    };

    await updatePublisherGame.mutateAsync(request);
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherGameStatus} status
   */
  const onChangeStatus = async (status) => {
    const previousStatus = publisherGame.status;

    if (status === "Signed" && previousStatus !== "Signed" && affectedPublisherCount > 0) {
      setPendingStatus(status);
      return;
    }

    if (status === "Declined" && previousStatus !== "Declined") {
      setShowDeclinedFeedbackModal(true);
      return;
    }

    await saveStatus(status);
  };

  const closeDialog = () => {
    setPendingStatus(null);
  };

  /**
   * @param {boolean|undefined} cleanUpOtherPublisherInterest
   */
  const savePendingStatus = async (cleanUpOtherPublisherInterest = undefined) => {
    if (!pendingStatus) {
      return;
    }

    await saveStatus(pendingStatus, cleanUpOtherPublisherInterest);
    closeDialog();
  };

  /**
   * @param {boolean|undefined} publisherProvidedFeedback
   */
  const savePublisherProvidedFeedback = async (publisherProvidedFeedback) => {
    await updatePublisherGame.mutateAsync({
      status: "Declined",
      publisherProvidedFeedback,
      rules: undefined,
      sellsheet: undefined,
      sizzle: undefined,
      sentPNP: undefined,
      externalPortalUrl: undefined,
      nextActionType: undefined,
      expectedReleaseDate: undefined,
      signedDate: undefined,
      cleanUpOtherPublisherInterest: undefined,
    });

    setShowDeclinedFeedbackModal(false);
  };

  return (
    <>
      <PublisherInterestStatusSelect value={publisherGame.status} onChange={onChangeStatus} />
      <FullScreenDialog title="Update other publisher records?" open={open} onClose={closeDialog}>
        <Stack spacing={2}>
          <Alert severity="info">
            You can bulk update other publisher records as part of this status update. All shortlisted publishers will
            be unlisted and all other in progress publishers will be moved to &quot;Lost&quot;. Lost is how we indicate
            a publisher missed out on the opportunity. No publishers will be contacted. We suggest you send a cursor
            email to let them know it&apos;s been signed. This will ensure they don&apos;t waste time looking at it.
          </Alert>

          {publishersToMarkLost.length > 0 && <PublisherCleanupList publishers={publishersToMarkLost} />}
          <CardActions>
            <Button variant="outlined" onClick={() => savePendingStatus()} disabled={updatePublisherGame.isPending}>
              {updatePublisherGame.isPending ? <CircularProgress size={CircularProgressSize} /> : "No, do not update"}
            </Button>
            <Button
              variant="contained"
              onClick={() => savePendingStatus(true)}
              disabled={updatePublisherGame.isPending}
            >
              {updatePublisherGame.isPending ? <CircularProgress size={CircularProgressSize} /> : "Yes, update"}
            </Button>
          </CardActions>
        </Stack>
      </FullScreenDialog>
      <FullScreenDialog
        title="Did you receive feedback from the publisher?"
        open={showDeclinedFeedbackModal}
        onClose={() => setShowDeclinedFeedbackModal(false)}
      >
        <Stack spacing={2}>
          <Typography>
            Please let us know if the publisher gave you feedback on your game. If you received a generic statement that
            your game is not a good fit, select &quot;No&quot;. We will use this information to help other designers
            understand whether they should expect feedback from this publisher.
          </Typography>
          <CardActions>
            <Button
              variant="outlined"
              onClick={() => savePublisherProvidedFeedback(false)}
              disabled={updatePublisherGame.isPending}
            >
              {updatePublisherGame.isPending ? <CircularProgress size={CircularProgressSize} /> : "No"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => savePublisherProvidedFeedback(undefined)}
              disabled={updatePublisherGame.isPending}
            >
              {updatePublisherGame.isPending ? <CircularProgress size={CircularProgressSize} /> : "Prefer not to say"}
            </Button>
            <Button
              variant="contained"
              onClick={() => savePublisherProvidedFeedback(true)}
              disabled={updatePublisherGame.isPending}
            >
              {updatePublisherGame.isPending ? <CircularProgress size={CircularProgressSize} /> : "Yes"}
            </Button>
          </CardActions>
        </Stack>
      </FullScreenDialog>
    </>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView[]} props.publishers
 */
const PublisherCleanupList = ({ publishers }) => {
  return (
    <List>
      {publishers.map((publisher) => (
        <ListItem key={publisher.id} divider dense>
          <ListItemText
            primary={publisher.publisherName}
            secondary={`Current status: ${PublisherGameStatusLabel[publisher.status] || publisher.status}`}
          />
          <Chip label={publisher.status === "ShortListed" ? "Will be unlisted" : "Will be moved to Lost"} />
        </ListItem>
      ))}
    </List>
  );
};
